import React, { useEffect, useMemo, useState } from "react";
import {
  EuiComboBox,
  EuiComboBoxOptionOption,
  EuiButton,
  EuiButtonEmpty,
  EuiFieldText,
  EuiFormRow,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiSelect,
  EuiSpacer,
  EuiToolTip,
} from "@elastic/eui";
import { useParams } from "react-router";
import {
  AnketaResourcesEnum,
  KeycloakUser,
  isValidEmail,
  useAnketaAuth,
  useApi,
  useToast,
} from "../../../../";
import { AnketaActionsEnum } from "src/auth";
import { ComplexQuestionEditorProps } from "../../../";
import { PhoneInput } from "../phone_input";
import { useAnketaI18n } from "src/internationalization";

const INTERNAL_DOMAINS = new Set(["medocino.net", "autonubil.de"]);

const dummyUser = (email: string): KeycloakUser =>
  ({ id: email, email, username: email } as unknown as KeycloakUser);

const optionFor = (
  user: KeycloakUser,
): EuiComboBoxOptionOption<KeycloakUser> => ({
  label:
    `${user.firstName ?? ""} ${user.lastName ?? ""} (${user.email})`.trim(),
  value: user,
});

const sameEmail = (a?: string, b?: string): boolean =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

// Parse the stored value (comma-joined e-mails, or a legacy CustomerContact
// object / JSON string) into a list of e-mail addresses.
const parseEmails = (raw: any): string[] => {
  if (!raw) return [];
  if (typeof raw === "object") return raw.email ? [String(raw.email)] : [];
  const s = String(raw).trim();
  if (s.startsWith("{")) {
    try {
      const obj = JSON.parse(s);
      return obj?.email ? [String(obj.email)] : [];
    } catch {
      /* fall through */
    }
  }
  return s
    .split(",")
    .map((e) => e.trim())
    .filter(Boolean);
};

// ---- Shared lookups --------------------------------------------------------
// The general section has one of these editors per customer role (sales,
// technical, organizational). Each used to fetch the same two lists itself;
// share one in-flight request per questionnaire instead, briefly cached.
const LOOKUP_TTL_MS = 60_000;
const lookupCache = new Map<string, { at: number; promise: Promise<any> }>();

const sharedLookup = <T,>(key: string, load: () => Promise<T>): Promise<T> => {
  const hit = lookupCache.get(key);
  if (hit && Date.now() - hit.at < LOOKUP_TTL_MS) {
    return hit.promise as Promise<T>;
  }
  const promise = load().catch((err) => {
    lookupCache.delete(key);
    throw err;
  });
  lookupCache.set(key, { at: Date.now(), promise });
  return promise;
};

// A user created in one role's editor must show up in the other roles'
// dropdowns right away - not only after a reload.
const USER_CREATED_EVENT = "anketa:customer-user-created";

/**
 * Customer contact editor: multi-select existing customer-organization users,
 * and/or create new managed Keycloak users in the customer organization
 * (resolved by e-mail domain). The field value is stored as a comma-joined list
 * of e-mail addresses.
 */
export const CustomerContactEditor = ({
  question,
  setValue,
  invalid,
}: ComplexQuestionEditorProps): JSX.Element => {
  const { catalogId, questionnaireId } = useParams();
  const { keycloak, checkPermission } = useAnketaAuth();
  const { Get, Post } = useApi();
  const { addToast } = useToast();
  const { translate } = useAnketaI18n();

  const [users, setUsers] = useState<KeycloakUser[]>([]);
  const [domains, setDomains] = useState<string[]>([]);
  const [selected, setSelected] = useState<
    EuiComboBoxOptionOption<KeycloakUser>[]
  >([]);
  const [loaded, setLoaded] = useState(false);
  const [creating, setCreating] = useState(false);
  // The create form is collapsed by default: with one editor per role it used
  // to fill the section with three always-open forms.
  const [formOpen, setFormOpen] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [local, setLocal] = useState("");
  const [domain, setDomain] = useState("");
  const [phone, setPhone] = useState("");
  const [jobTitle, setJobTitle] = useState("");

  const canFetch = !!keycloak?.token && !!catalogId && !!questionnaireId;
  const base = `v2/${AnketaResourcesEnum.questionnaires}/${catalogId}/${questionnaireId}`;
  // UI affordance only - the POST route enforces create rights (+ membership)
  // itself. Without them the form could only ever fail with a 403.
  const canCreate =
    !question.readonly &&
    checkPermission(AnketaResourcesEnum.questionnaires, AnketaActionsEnum.create);

  const emit = (opts: EuiComboBoxOptionOption<KeycloakUser>[]): void => {
    setValue(
      opts
        .map((o) => o.value?.email ?? o.label)
        .filter(Boolean)
        .join(","),
    );
  };

  // Existing customer-org users for the dropdown.
  useEffect(() => {
    if (!canFetch) return;
    sharedLookup(`${base}/organizations/customers`, () =>
      Get<Array<KeycloakUser>>({
        path: `${base}/organizations/customers`,
        responseType: "json",
        token: keycloak.token,
      }).then((r) => (Array.isArray(r.data) ? r.data.filter(Boolean) : [])),
    )
      .then((list) => setUsers(list))
      .catch(() => setUsers([]))
      .finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch]);

  // Customer organization domains (excluding internal) for the new-contact form.
  useEffect(() => {
    if (!canFetch) return;
    sharedLookup(`${base}/organizations`, () =>
      Get<Array<any>>({
        path: `${base}/organizations`,
        responseType: "json",
        token: keycloak.token,
      }).then((r) => {
        const orgs = Array.isArray(r.data) ? r.data.filter(Boolean) : [];
        return Array.from(
          new Set(
            orgs
              .flatMap((o: any) => (o?.domains ?? []).map((d: any) => d?.name))
              .filter((d: any): d is string => Boolean(d))
              .map((d: string) => d.toLowerCase())
              .filter((d: string) => !INTERNAL_DOMAINS.has(d)),
          ),
        ) as string[];
      }),
    )
      .then((found) => {
        setDomains(found);
        setDomain((cur) => cur || (found.length > 0 ? found[0] : ""));
      })
      .catch(() => setDomains([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch]);

  // Pick up users created in a sibling role editor.
  useEffect(() => {
    const onCreated = (e: Event): void => {
      const { base: from, user } = (e as CustomEvent).detail ?? {};
      if (from !== base || !user) return;
      setUsers((prev) =>
        prev.some((u) => sameEmail(u.email, user.email)) ? prev : [user, ...prev],
      );
    };
    window.addEventListener(USER_CREATED_EVENT, onCreated);
    return () => window.removeEventListener(USER_CREATED_EVENT, onCreated);
  }, [base]);

  // Initialise the selection from the stored value once users are loaded.
  useEffect(() => {
    if (!loaded) return;
    const emails = parseEmails(question.fact?.raw);
    const opts = emails.map((email) => {
      const user = users.find((u) => sameEmail(u.email, email));
      return optionFor(user ?? dummyUser(email));
    });
    setSelected(opts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  const options = useMemo<EuiComboBoxOptionOption<KeycloakUser>[]>(
    () => users.map(optionFor),
    [users],
  );

  const onChange = (
    opts: EuiComboBoxOptionOption<KeycloakUser>[],
  ): void => {
    setSelected(opts);
    emit(opts);
  };

  const email = local.trim() && domain ? `${local.trim()}@${domain}` : "";
  const emailValid = isValidEmail(email);
  const existingUser = emailValid
    ? users.find((u) => sameEmail(u.email, email))
    : undefined;
  const emailError =
    emailTouched && local.trim() !== "" && !emailValid
      ? translate("anketa.questionnaire.editor.contact.invalidEmail.text", {
          defaultMessage:
            "Please choose a valid local part and a customer domain.",
        })
      : undefined;

  // People paste whole addresses into the local-part field: split them, and
  // pick the domain if it is one of the customer's.
  const onLocalChange = (value: string): void => {
    const at = value.indexOf("@");
    if (at >= 0) {
      const pastedDomain = value.slice(at + 1).trim().toLowerCase();
      setLocal(value.slice(0, at));
      if (domains.includes(pastedDomain)) {
        setDomain(pastedDomain);
      }
      setEmailTouched(true);
      return;
    }
    setLocal(value);
  };

  const resetForm = (): void => {
    setFirstName("");
    setLastName("");
    setLocal("");
    setPhone("");
    setJobTitle("");
    setEmailTouched(false);
  };

  const addToSelection = (user: KeycloakUser): void => {
    const next = selected.some((o) => sameEmail(o.value?.email, user.email))
      ? selected
      : [...selected, optionFor(user)];
    setSelected(next);
    emit(next);
  };

  const createUser = async (): Promise<void> => {
    setEmailTouched(true);
    if (!emailValid || creating) {
      return;
    }
    // Already a customer user: just add it, no round trip needed.
    if (existingUser) {
      addToSelection(existingUser);
      resetForm();
      setFormOpen(false);
      return;
    }
    setCreating(true);
    const { data, error, status } = await Post<KeycloakUser>({
      path: `${base}/organizations/customers/users`,
      responseType: "json",
      token: keycloak.token,
      value: { email, firstName, lastName, phone, jobTitle },
    });
    setCreating(false);
    if (error || !data) {
      const svc: any = (error as any)?.service;
      const detail =
        (svc && typeof svc.data === "string" && svc.data) ||
        svc?.error?.message ||
        (typeof error === "string" ? error : (error as any)?.message);
      addToast({
        title: translate(
          "anketa.questionnaire.editor.contact.createFailed.title",
          { defaultMessage: "Could not create user" },
        ),
        color: "danger",
        text:
          detail ||
          translate("anketa.questionnaire.editor.contact.createFailed.code", {
            values: { code: status ?? "?" },
            defaultMessage: "Error code {{code}}",
          }),
      });
      return;
    }
    const created = data as KeycloakUser;
    setUsers((prev) =>
      prev.some((u) => sameEmail(u.email, created.email))
        ? prev
        : [created, ...prev],
    );
    lookupCache.delete(`${base}/organizations/customers`);
    window.dispatchEvent(
      new CustomEvent(USER_CREATED_EVENT, { detail: { base, user: created } }),
    );
    addToSelection(created);
    addToast({
      title: translate("anketa.questionnaire.editor.contact.created.title", {
        defaultMessage: "Customer user created",
      }),
      color: "success",
      text: created.email,
    });
    resetForm();
    setFormOpen(false);
  };

  // Enter in any field of the create form submits it.
  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "Enter") {
      e.preventDefault();
      void createUser();
    }
  };

  const noDomainText = translate(
    "anketa.questionnaire.editor.contact.noDomain.callout",
    {
      defaultMessage:
        "No customer organization/domain found for this questionnaire.",
    },
  );

  const openButton = (
    <EuiButtonEmpty
      size="s"
      iconType="plusInCircle"
      flush="left"
      onClick={() => setFormOpen(true)}
      isDisabled={loaded && domains.length === 0}
    >
      {translate("anketa.questionnaire.editor.contact.create.open", {
        defaultMessage: "Create new contact",
      })}
    </EuiButtonEmpty>
  );

  return (
    <EuiFlexItem grow style={{ minWidth: "260px" }}>
      <EuiComboBox
        aria-label={translate(
          "anketa.questionnaire.editor.contact.select.title",
          { defaultMessage: "Select contacts" },
        )}
        placeholder={translate(
          "anketa.questionnaire.editor.contact.select.placeholder",
          { defaultMessage: "Search customer users…" },
        )}
        options={options}
        selectedOptions={selected}
        onChange={onChange}
        isClearable
        isDisabled={question.readonly}
        isInvalid={invalid}
        isLoading={!loaded}
        fullWidth
      />

      {canCreate && !formOpen && (
        <>
          <EuiSpacer size="xs" />
          {loaded && domains.length === 0 ? (
            <EuiToolTip content={noDomainText}>{openButton}</EuiToolTip>
          ) : (
            openButton
          )}
        </>
      )}

      {canCreate && formOpen && (
        <>
          <EuiSpacer size="s" />
          <EuiPanel color="subdued" paddingSize="m" hasShadow={false}>
            <EuiFlexGroup gutterSize="s">
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(
                    "anketa.questionnaire.editor.contact.firstName.label",
                    { defaultMessage: "First name" },
                  )}
                  fullWidth
                >
                  <EuiFieldText
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    onKeyDown={onKeyDown}
                    autoComplete="off"
                    autoFocus
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(
                    "anketa.questionnaire.editor.contact.lastName.label",
                    { defaultMessage: "Last name" },
                  )}
                  fullWidth
                >
                  <EuiFieldText
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    onKeyDown={onKeyDown}
                    autoComplete="off"
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
            </EuiFlexGroup>

            <EuiFormRow
              label={translate(
                "anketa.questionnaire.editor.contact.email.label",
                { defaultMessage: "E-mail" },
              )}
              fullWidth
              isInvalid={!!emailError}
              error={emailError}
              helpText={
                existingUser
                  ? translate(
                      "anketa.questionnaire.editor.contact.exists.help",
                      {
                        defaultMessage:
                          "This contact already exists and will just be added.",
                      },
                    )
                  : undefined
              }
            >
              <EuiFlexGroup gutterSize="s" alignItems="center" responsive={false}>
                <EuiFlexItem>
                  <EuiFieldText
                    placeholder={translate(
                      "anketa.questionnaire.editor.email.local.placeholder",
                      { defaultMessage: "firstname.lastname" },
                    )}
                    value={local}
                    onChange={(e) => onLocalChange(e.target.value)}
                    onBlur={() => setEmailTouched(true)}
                    onKeyDown={onKeyDown}
                    isInvalid={!!emailError}
                    autoComplete="off"
                    fullWidth
                    // A single customer domain needs no picker.
                    append={domains.length === 1 ? `@${domains[0]}` : undefined}
                    aria-label={translate(
                      "anketa.questionnaire.editor.contact.email.local.aria",
                      { defaultMessage: "E-mail local part" },
                    )}
                  />
                </EuiFlexItem>
                {domains.length > 1 && (
                  <EuiFlexItem grow={false} style={{ minWidth: 180 }}>
                    <EuiSelect
                      options={domains.map((d) => ({ value: d, text: `@${d}` }))}
                      value={domain}
                      onChange={(e) => setDomain(e.target.value)}
                      aria-label={translate(
                        "anketa.questionnaire.editor.email.domain.aria",
                        { defaultMessage: "Customer domain" },
                      )}
                    />
                  </EuiFlexItem>
                )}
              </EuiFlexGroup>
            </EuiFormRow>

            <EuiFlexGroup gutterSize="s">
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(
                    "anketa.questionnaire.editor.contact.phone.label",
                    { defaultMessage: "Phone" },
                  )}
                  fullWidth
                >
                  <PhoneInput value={phone} onChange={(v) => setPhone(v)} />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(
                    "anketa.questionnaire.editor.contact.jobTitle.label",
                    { defaultMessage: "Position" },
                  )}
                  fullWidth
                >
                  <EuiFieldText
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    onKeyDown={onKeyDown}
                    autoComplete="off"
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
            </EuiFlexGroup>

            <EuiSpacer size="m" />
            <EuiFlexGroup
              justifyContent="flexEnd"
              gutterSize="s"
              responsive={false}
            >
              <EuiFlexItem grow={false}>
                <EuiButtonEmpty
                  size="s"
                  onClick={() => {
                    resetForm();
                    setFormOpen(false);
                  }}
                >
                  {translate("anketa.questionnaire.editor.contact.create.cancel", {
                    defaultMessage: "Cancel",
                  })}
                </EuiButtonEmpty>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiButton
                  size="s"
                  fill
                  iconType={existingUser ? "plus" : "plusInCircle"}
                  onClick={createUser}
                  isLoading={creating}
                  isDisabled={!emailValid}
                >
                  {existingUser
                    ? translate(
                        "anketa.questionnaire.editor.contact.addExisting.button",
                        { defaultMessage: "Add existing contact" },
                      )
                    : translate(
                        "anketa.questionnaire.editor.contact.create.button",
                        { defaultMessage: "Create user & add" },
                      )}
                </EuiButton>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiPanel>
        </>
      )}
    </EuiFlexItem>
  );
};
